/**
 * THE TRIAL CONVERSION VOCABULARY (Miles, 2026-10-20 and 2026-10-26): a card starts the evaluation, and the plan chosen
 * at signup starts when it ends. Design records: docs/PLAN-trial-card-on-file-20261020.md and
 * docs/PLAN-per-employee-pricing-20261030.md (rulings R8, R10, R11; contracts C-7..C-13, C-18).
 *
 * VERBATIM TWINS: functions/src/billing/trialConversionVocabulary.ts and src/types/trialConversion.ts are
 * BYTE-IDENTICAL, pinned by tests/billing/trialConversionVocabulary.parity.test.ts. One owner per vocabulary: the
 * server writes these words and the trial page reads them; neither side keeps a list of its own.
 *
 * Codes, numbers and shapes only. Pure and dependency-free (no `satisfies`: the page's build is CRA's Babel).
 */

/**
 * The disclosed set a person agrees to on the card step, beside the automatic-renewal terms version. Bumped when what
 * the screen DISCLOSES changes (a new fact, or one removed), never for a copy edit: it rides the retained
 * acknowledgment so a later reader can tell which facts this person was shown.
 *
 * 2026-11-01.1: the plan is the one chosen at signup (any count, monthly, annual billed monthly, or a prepaid year, on
 * the edition then sold), with a signed evaluation Order for a commitment, instead of one Prest0n monthly; and the
 * free period is the free internship (main's 2026-10-30.1, Justin), whose terms the same screen shows.
 *
 * 2026-11-04.1 (contract C-4, edition 2026-12, R16): the plan is priced on the 2026-12 ladder (monthly by team size, a
 * prepaid year up to 20% off); a page from another edition is a version behind and asks again.
 */
export const TRIAL_CONVERSION_TERMS_VERSION = '2026-11-04.1';

/**
 * The record's fixed key set (written whole, every key present, null when empty): the map `trial_conversion` on the
 * plan document. `tier`, `seats`, `cadence`, `commitment_months` and `catalog_edition` are the plan the person chose
 * and agreed to (C-7), null until they choose one; `order_id` names the signed evaluation Order a commitment carries.
 * One `sent_<kind>_ms` per notice kind.
 */
export const TRIAL_CONVERSION_RECORD_KEYS = [
  'v', 'required', 'state', 'account_id', 'trial_days',
  'tier', 'seats', 'cadence', 'commitment_months', 'catalog_edition', 'order_id', 'per_seat_cents',
  'consent_id', 'armed_by_uid', 'armed_at_ms',
  'payment_method_id', 'card_brand', 'card_last4', 'card_exp_month', 'card_exp_year', 'time_zone', 'charge_at_ms',
  'lease_until_ms', 'subscription_id', 'invoice_id', 'attempts', 'declined_at_ms', 'last_failure_code',
  'consent_reason', 'sent_armed_ms', 'sent_reminder_7d_ms', 'sent_reminder_3d_ms', 'sent_reminder_1d_ms',
  'sent_converted_ms', 'sent_declined_ms', 'sent_declined_last_call_ms', 'sent_turned_off_ms',
  'sent_needs_consent_ms', 'sent_invite_card_ms', 'sent_extended_ms', 'updated_at_ms',
] as const;
export type TrialConversionRecordKey = (typeof TRIAL_CONVERSION_RECORD_KEYS)[number];

/**
 * A conversion's life.
 *   awaiting_card  a trial that needs a card before any work (stamped when the trial starts)
 *   armed          card, plan and consent on file: the plan starts when the evaluation ends
 *   off            the person said "don't start my plan": the evaluation runs to its end, nothing is charged
 *   charging       the sweep holds it while it talks to Stripe
 *   converted      the first charge is paid; the plan is live
 *   declined       the first charge failed; it can still be paid on the page until Stripe's window closes
 *   needs_consent  the price, the terms, the card or who may pay changed: nothing is charged until someone confirms
 *   lapsed         the evaluation ended with nothing to charge (no card, turned off, a declined charge never paid, a
 *                  warning that could not be delivered, a signed hire still going through, or charging held off)
 *   superseded     a signed hire started the plan first
 */
export const TRIAL_CONVERSION_STATES = [
  'awaiting_card', 'armed', 'off', 'charging', 'converted', 'declined', 'needs_consent', 'lapsed', 'superseded',
] as const;
export type TrialConversionState = (typeof TRIAL_CONVERSION_STATES)[number];

export const TRIAL_CONVERSION_TERMINAL_STATES = ['converted', 'lapsed', 'superseded'] as const;

/** Every move a conversion may make; anything else is refused. armed → armed is a new card or a changed plan. */
export const TRIAL_CONVERSION_TRANSITIONS: Readonly<Record<TrialConversionState, readonly TrialConversionState[]>> = {
  awaiting_card: ['armed', 'lapsed', 'superseded'],
  armed: ['armed', 'off', 'charging', 'needs_consent', 'lapsed', 'superseded'],
  off: ['armed', 'lapsed', 'superseded'],
  charging: ['converted', 'declined', 'armed', 'needs_consent', 'superseded'],
  declined: ['converted', 'lapsed', 'superseded'],
  needs_consent: ['armed', 'off', 'lapsed', 'superseded'],
  converted: [],
  lapsed: [],
  superseded: [],
};

export function canTrialConversionMove(from: TrialConversionState, to: TrialConversionState): boolean {
  return (TRIAL_CONVERSION_TRANSITIONS[from] || []).indexOf(to) >= 0;
}

/** The only state that refuses work: a trial that needs a card and has none yet. "off" never ends a trial early. */
export const TRIAL_CONVERSION_BLOCKING_STATES = ['awaiting_card'] as const;

/**
 * The plan starts AFTER the evaluation (the sweep runs every ten minutes), so an armed evaluation keeps working past
 * its end until its first charge resolves — at most this long. A decline pauses work at once (no grace).
 */
export const TRIAL_CONVERSION_CHARGE_WINDOW_STATES = ['armed', 'charging'] as const;

/** The states in which a conversion has its card and plan in hand (an armed evaluation holds its larger team, C-18). */
export const TRIAL_CONVERSION_ARMED_STATES = ['armed', 'charging'] as const;

/** Why a conversion waits for someone to confirm again. */
export const TRIAL_CONVERSION_CONSENT_REASONS = ['price-changed', 'terms-changed', 'card-removed', 'card-expired', 'authority-lost'] as const;
export type TrialConversionConsentReason = (typeof TRIAL_CONVERSION_CONSENT_REASONS)[number];

/**
 * What the platform tells a person, and when (D18: notices at set times before a charge the person agreed to).
 *   armed               the acknowledgment: the plan, the terms accepted, the date, the amount, the card, how to cancel
 *   reminder_7d/3d/1d   before the evaluation ends (the 7-day one only for evaluations longer than 8 days)
 *   converted           the receipt: "your plan has started"
 *   declined            the first charge failed; one tap to fix
 *   declined_last_call  still unpaid, before Stripe's window closes
 *   turned_off          "you won't be charged"
 *   needs_consent       nothing will be charged until someone confirms again
 *   invite_card         an evaluation running without a card: add one to keep Prest0n working (never required)
 *   extended            a code moved the evaluation's end, and the plan's start with it
 */
export const TRIAL_CONVERSION_NOTICE_KINDS = [
  'armed', 'reminder_7d', 'reminder_3d', 'reminder_1d', 'converted', 'declined', 'declined_last_call', 'turned_off',
  'needs_consent', 'invite_card', 'extended',
] as const;
export type TrialConversionNoticeKind = (typeof TRIAL_CONVERSION_NOTICE_KINDS)[number];

/** The notices that also go by text, where the person confirmed a number. Email and the bell carry every one. */
export const TRIAL_CONVERSION_TEXTED_KINDS = ['reminder_3d', 'reminder_1d', 'converted', 'declined', 'declined_last_call'] as const;

export const HOUR_MS = 3_600_000;
export const DAY_MS = 86_400_000;

/**
 * When each reminder's window opens, before the plan starts; it runs to the next closer one's. Reminders wait for the
 * person's daytime inside their window, so the "7-day" one opens a day early: it still lands at least 7 days ahead.
 * Every reminder names the date, never "in N days".
 */
export const TRIAL_REMINDER_LEADS_MS: Readonly<Record<'reminder_7d' | 'reminder_3d' | 'reminder_1d', number>> = {
  reminder_7d: 8 * DAY_MS,
  reminder_3d: 3 * DAY_MS,
  reminder_1d: 1 * DAY_MS,
};

/**
 * No warning, no charge (C-12; Visa): no automatic charge sooner than this after the arming acknowledgment was
 * delivered, and, for an evaluation longer than SHORT_TRIAL_MAX_MS, after the 7-day reminder too. Otherwise the
 * evaluation's end and the charge move together to the first moment the rule allows; those days are free. Arming
 * again (a new card, the plan turned back on, a changed plan) sends a new acknowledgment, and the rule counts from it.
 */
export const TRIAL_WARNING_LEAD_MS = 7 * DAY_MS;

/**
 * Arming sets the plan's start no sooner than the lead plus this margin after the moment it arms: the acknowledgment
 * leaves a moment after arming, and the start must still be a full lead after it was delivered.
 */
export const ACK_DELIVERY_MARGIN_MS = 1 * HOUR_MS;

/**
 * A conversion armed this close to its charge (or closer) is warned by its acknowledgment, which already says when and
 * how much; one armed earlier is also warned by the 7-day reminder (Visa: trials longer than 7 days get a reminder).
 */
export const SHORT_TRIAL_MAX_MS = 8 * DAY_MS;

/** How long an ended, armed evaluation keeps working while its first charge resolves. */
export const TRIAL_CONVERSION_CHARGE_WINDOW_MS = 2 * HOUR_MS;

/** Stripe keeps an unpaid first invoice payable for 23 hours; the last call goes before that. */
export const DECLINED_PAY_WINDOW_MS = 23 * HOUR_MS;
export const DECLINED_LAST_CALL_AFTER_MS = 20 * HOUR_MS;

/** An evaluation running without a card is invited to add one this long before it ends (once). */
export const INVITE_CARD_LEAD_MS = 3 * DAY_MS;

/** Texts land only in these local hours [start, end), in the time zone recorded when the card was added. */
export const TEXT_HOURS_START = 9;
export const TEXT_HOURS_END = 20;

/**
 * What an endpoint answers when it refuses; the server sends the words, the page keeps a fallback per code. The plan's
 * own refusals are the resolver's (C-2): bad-cadence, bad-seats (400), by-meeting, tier-mismatch, edition-not-on-sale
 * (409). no-plan: arming named no plan. order-required / order-invalid: a commitment needs its signed evaluation Order.
 * evaluation-used: this person or this card has had an organization's evaluation already. waived: a founders' code
 * started this evaluation without a card, so it never arms a charge.
 */
export const TRIAL_CONVERSION_REFUSALS = [
  'not-signed-in', 'not-allowed', 'not-offered', 'no-trial', 'trial-ended', 'plan-exists', 'hire-pending',
  'granted-plan', 'exempt', 'setup-incomplete', 'card-wrong-customer', 'card-expired', 'consent-absent',
  'consent-malformed', 'consent-stale-version', 'consent-mismatch', 'terms-not-accepted', 'not-armed', 'charging',
  'nothing-to-pay', 'pay-window-closed', 'card-declined', 'unavailable',
  'no-plan', 'bad-cadence', 'bad-seats', 'by-meeting', 'tier-mismatch', 'edition-not-on-sale',
  'order-required', 'order-invalid', 'evaluation-used', 'waived',
] as const;
export type TrialConversionRefusal = (typeof TRIAL_CONVERSION_REFUSALS)[number];

/** The plan's refusals the resolver answers (C-2), and what each carries for the page's re-quote. */
export const TRIAL_PLAN_REFUSALS = ['no-plan', 'bad-cadence', 'bad-seats', 'by-meeting', 'tier-mismatch', 'edition-not-on-sale'] as const;
export type TrialPlanRefusal = (typeof TRIAL_PLAN_REFUSALS)[number];

/** The card a conversion charges, as the page draws it. Never the payment method's id. */
export interface TrialConversionCard {
  brand: string;
  last4: string;
  expMonth: number;
  expYear: number;
}

/**
 * The plan that starts when the evaluation ends, exactly as the server resolved it (the tier vocabulary's resolveHire
 * against the edition sold to this account, or the recorded plan on the edition it was signed on). Integer cents. Its
 * words (a tier id, a cadence, an edition) are the tier vocabulary's (planTiers.ts, PLAN_CADENCES and CATALOG_EDITIONS),
 * never a list of this module's own.
 */
export interface TrialConversionPlan {
  tier: string;
  /** monthly, annual (a 12-month commitment billed monthly) or prepay (a prepaid year). */
  cadence: string;
  seats: number;
  /** Per Prest0n employee, per billing interval (a month; a year for a prepaid year). */
  perSeatCents: number;
  /** What each charge is: perSeatCents × seats. A prepaid year is charged in full when the evaluation ends. */
  amountCents: number;
  interval: 'month' | 'year';
  /** The binding term in months; 0 for monthly. */
  commitmentMonths: number;
  /** pr0 a month per Prest0n employee the tier is priced in. */
  unitPr0: number;
  /** pr0 a month for the whole team. */
  allocationPr0: number;
  /** The published ladder the tier belongs to. */
  edition: string;
}

/** Why the plan asked about cannot start here, with what the page needs to re-quote it. */
export interface TrialPlanRefusalFacts {
  code: TrialPlanRefusal;
  maxSeats?: number;
  expectedTier?: string;
  expectedPerSeatCents?: number;
}

/** What the trial page asks the server (trialConversionStatus) before it draws anything. */
export interface TrialConversionStatus {
  accountId: string;
  /** The lever offers the screen to this account. */
  offered: boolean;
  /** This trial must have a card before any work. */
  required: boolean;
  /** A trial this caller starts now would need a card first: onboarding shows its card step on this answer. */
  startRequiresCard: boolean;
  /** null: no conversion record (a trial from before cards, or no trial at all). */
  state: TrialConversionState | null;
  trialActive: boolean;
  trialEndsAtMs: number | null;
  trialDays: number | null;
  trialCreditCents: number | null;
  /** When the plan starts: the evaluation's end, or later when the 7-day rule moved it (C-12). */
  chargeAtMs: number | null;
  /**
   * When the plan would start if it were armed now: a required evaluation's days from now, any other's own end — and
   * never sooner than seven days after the acknowledgment arming sends (C-12). null when no evaluation is running.
   */
  armChargeAtMs: number | null;
  /** The edition the server sells this account a new plan on now (C-3). */
  sellingEdition: string;
  /** The published edition (the one the public pages and onboarding's offer show) is the one on sale here. */
  publishedOnSale: boolean;
  /**
   * The plan: the recorded one while it stands (armed, off, confirm again, declined), else the one this request asked
   * about (`cadence` and `seats`), resolved by the server. null: none chosen yet, or the asked one cannot start here.
   */
  plan: TrialConversionPlan | null;
  /** Why the asked plan cannot start here, when it cannot. */
  planRefusal: TrialPlanRefusalFacts | null;
  /** The signed evaluation Order the recorded plan carries, if any. */
  orderId: string | null;
  /**
   * The plan starts by itself when the evaluation ends: the lever charges for the person who armed it (or would arm it
   * now). False: nothing starts automatically — the page says the plan can be started at the end in one tap.
   */
  autoStart: boolean;
  card: TrialConversionCard | null;
  /** The versions the page must send back with its consent. */
  renewalTermsVersion: string;
  trialTermsVersion: string;
  /** A declined first charge that can still be paid on the page, until when. */
  payableUntilMs: number | null;
  consentReason: TrialConversionConsentReason | null;
  /** The caller may add or change the card, or turn the plan's start off and on. */
  canManage: boolean;
  /**
   * The card to offer where the page asks for one (never while armed: a change is a new card): the conversion's own
   * while it stands, else the billing profile's — never one that will have expired when the plan starts.
   */
  savedCard: TrialConversionSavedCard | null;
}

/** A card already on the billing profile, offered so the person need not type it again. */
export interface TrialConversionSavedCard extends TrialConversionCard {
  paymentMethodId: string;
}

/** What the page sends when the person accepts and presses "start my evaluation". */
export interface TrialArmRequest {
  accountId: string;
  /** The plan, as the server quoted it (C-8): its tier and team size. */
  tier: string;
  seats: number;
  /** The signed evaluation Order a commitment (annual or a prepaid year) carries. */
  orderId?: string | null;
  /** A card just entered (a confirmed SetupIntent on the billing profile), or one already on it. */
  setupIntentId?: string;
  paymentMethodId?: string;
  /** The automatic-renewal consent, in the shape billing/autoRenewalDisclosure.ts verifies. */
  autoRenewalConsent: {
    tier: string;
    seats: number;
    perSeatCents: number;
    interval: 'month' | 'year';
    commitmentMonths: number;
    acceptedAt: string;
    termsVersion: string;
  };
  trialTermsVersion: string;
  /** The Terms and Privacy box: the versions the page showed. */
  terms: { tosVersion: string; privacyVersion: string; acceptedAtMs: number };
  /** The person's IANA time zone, so dates in emails and the hours of texts are theirs. */
  timeZone: string | null;
}

/** The address of the card step, and the two facts of a chosen plan it carries (onboarding's offer → the card step). */
export const TRIAL_CARD_STEP_PATH = '/subscription/trial';
export const TRIAL_PLAN_PARAMS = { cadence: 'cadence', seats: 'seats' } as const;
